import { CREDITS_VISIBLE } from "../lib/credits";
import { MoreHorizontal } from "lucide-react";
import type { MatchAction, MatchCard } from "@hub/shared";
import { Button, IconButton, Menu, Pill, relativeTime } from "@/ui";
import { badgeTone, primaryAction, scoreText, scoreTone, secondaryActions } from "../lib/cards";
import { CardAvatar } from "./PartyAvatar";
import { PerspectiveHelp } from "./PerspectiveHelp";
import s from "../network.module.css";

export interface MatchRowProps {
  card: MatchCard;
  onOpen: (card: MatchCard) => void;
  /** Runs an action from the row. Actions that need a note or cost credits open the detail instead. */
  onAction: (card: MatchCard, action: MatchAction) => void;
  compact?: boolean;
}

export function MatchRow({ card, onOpen, onAction, compact }: MatchRowProps) {
  const primary = primaryAction(card);
  const rest = secondaryActions(card, primary);
  const masked = card.needer.hidden || card.helper.hidden;
  return (
    <div
      className={s.matchRow}
      data-compact={compact ? "true" : undefined}
      role="button"
      tabIndex={0}
      data-testid={`match-row-${card.matchId}`}
      onClick={() => onOpen(card)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(card);
        }
      }}
    >
      <CardAvatar avatar={card.avatar} size={compact ? 30 : 36} />
      <div className={s.matchMain}>
        <div className={s.matchTitle}>
          <span>{card.display.title}</span>
          <Pill tone={badgeTone(card.display.badgeColor)} title={card.scenarioName}>
            {card.display.badge}
          </Pill>
        </div>
        <div className={s.matchSubtitle}>{card.display.subtitle}</div>
        {!compact && (
          <div className={s.matchMeta}>
            <Pill tone={scoreTone(card.scoreLabel)} title={`Score ${card.score.toFixed(2)}`}>
              {scoreText(card.scoreLabel, card.score)}
            </Pill>
            <span className={`${s.muted} ${s.small}`}>{card.need.title}</span>
            <span className={`${s.muted} ${s.small}`} title={card.updated}>
              {relativeTime(card.updated)}
            </span>
          </div>
        )}
      </div>
      <div className={s.matchActions} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        {masked && <PerspectiveHelp card={card} compact />}
        {primary && (
          <Button size="sm" variant="primary" onClick={() => onAction(card, primary)} title={CREDITS_VISIBLE ? primary.creditLabel : undefined}>
            {primary.label}
          </Button>
        )}
        {rest.length > 0 && (
          <Menu
            align="right"
            trigger={
              <IconButton label="More actions">
                <MoreHorizontal size={16} />
              </IconButton>
            }
            items={rest.map((a) => ({
              id: a.id,
              label: CREDITS_VISIBLE && a.creditLabel && a.creditLabel !== "Free" ? `${a.label} (${a.creditLabel})` : a.label,
              danger: a.id === "dismiss" || a.id === "decline",
              disabled: a.disabled,
              onSelect: () => onAction(card, a),
            }))}
          />
        )}
      </div>
    </div>
  );
}
